import type { Component } from '../router';
import { navigate } from '../router';
import { icon } from '../components/icons';
import { confirmDialog } from '../components/ConfirmDialog';
import { renderBuildStamp } from '../components/BuildStamp';
import { currentUser, logout } from '../services/auth';
import { getStoredTheme, setTheme, type Theme } from '../utils/theme';

/**
 * Me — the account hub behind the phone tab bar's "Me" tab (also routable
 * on desktop). Identity summary, account menu (Devices & sessions,
 * Appearance), and sign-out. The tab used to deep-link straight to
 * /settings/devices, which dropped users onto a security screen with red
 * Revoke buttons — disorienting as a "Me" destination; this hub gives the
 * sub-pages a home.
 *
 * Identity is deliberately light: /api/me returns only userId +
 * deviceClass (no email/name yet) — enriching it is a backend follow-up.
 */

const THEMES: { value: Theme; label: string }[] = [
  { value: 'auto', label: 'Auto' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

export function signedInCopy(deviceClass: string | undefined): string {
  if (deviceClass === 'phone') return 'Signed in on this phone.';
  if (deviceClass === 'tablet') return 'Signed in on this tablet.';
  if (deviceClass === 'desktop') return 'Signed in on this computer.';
  return 'Signed in.';
}

function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (text !== undefined) el.textContent = text;
  return el;
}

export class MeView implements Component {
  private root: HTMLElement | null = null;

  mount(host: HTMLElement): void {
    const section = node('section', 'me');

    const header = node('header', 'me__header');
    header.append(
      node('h1', 'me__title', 'Me'),
      node('p', 'me__sub', signedInCopy(currentUser()?.deviceClass)),
    );

    const account = node('nav', 'me__group');
    account.setAttribute('aria-label', 'Account');
    account.append(this.deviceRow(), this.passkeyRow(), this.appearanceRow());

    const danger = node('div', 'me__group');
    danger.append(this.signOutRow());

    section.append(header, account, danger);
    renderBuildStamp(section, 'me__build-stamp');
    host.append(section);
    this.root = section;
  }

  unmount(): void {
    this.root?.remove();
    this.root = null;
  }

  private deviceRow(): HTMLElement {
    const a = node('a', 'me__row') as HTMLAnchorElement;
    a.href = '/settings/devices';
    const iconBox = node('span', 'me__row-icon');
    iconBox.appendChild(icon('devices'));
    const chevron = node('span', 'me__row-chevron');
    chevron.appendChild(icon('chevron-right'));
    a.append(iconBox, node('span', 'me__row-label', 'Devices & sessions'), chevron);
    return a;
  }

  private passkeyRow(): HTMLElement {
    const a = node('a', 'me__row') as HTMLAnchorElement;
    a.href = '/settings/passkeys';
    const iconBox = node('span', 'me__row-icon');
    iconBox.appendChild(icon('passkey'));
    const chevron = node('span', 'me__row-chevron');
    chevron.appendChild(icon('chevron-right'));
    a.append(iconBox, node('span', 'me__row-label', 'Passkeys'), chevron);
    return a;
  }

  private appearanceRow(): HTMLElement {
    const row = node('div', 'me__row me__row--static');
    const iconBox = node('span', 'me__row-icon');
    iconBox.appendChild(icon('appearance'));

    const segment = node('span', 'me__segment');
    segment.setAttribute('role', 'group');
    segment.setAttribute('aria-label', 'Theme');
    const buttons: HTMLButtonElement[] = [];
    const current = getStoredTheme();
    for (const { value, label } of THEMES) {
      const btn = node('button', 'me__segment-btn', label);
      btn.type = 'button';
      btn.dataset.theme = value;
      btn.setAttribute('aria-pressed', String(value === current));
      btn.addEventListener('click', () => {
        setTheme(value);
        for (const b of buttons) b.setAttribute('aria-pressed', String(b === btn));
      });
      buttons.push(btn);
      segment.appendChild(btn);
    }

    row.append(iconBox, node('span', 'me__row-label', 'Appearance'), segment);
    return row;
  }

  private signOutRow(): HTMLElement {
    const btn = node('button', 'me__row me__row--danger');
    btn.type = 'button';
    const iconBox = node('span', 'me__row-icon');
    iconBox.appendChild(icon('sign-out'));
    btn.append(iconBox, node('span', 'me__row-label', 'Sign out'));
    btn.addEventListener('click', () => void this.onSignOut(btn));
    return btn;
  }

  private async onSignOut(btn: HTMLButtonElement): Promise<void> {
    const ok = await confirmDialog({
      title: 'Sign out?',
      message: 'You will need a new magic link to sign back in on this device.',
      confirmLabel: 'Sign out',
      cancelLabel: 'Cancel',
      tone: 'danger',
    });
    if (!ok) return;
    btn.disabled = true;
    // logout() clears local auth state even when the network call fails,
    // so the bounce to /login is unconditional and a network error is
    // not worth keeping the user on a signed-out page for.
    try {
      await logout();
    } catch {
      // ignore — local state already cleared
    } finally {
      navigate('/login');
    }
  }
}

export function MePage(): Component {
  return new MeView();
}
